import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { ChatbotService } from '../../../services/chatbot-services/chatbot.service';
import { SelectedModuleService } from '../../../services/selected-module-service/selected-module.service';
import { ShowSourceService } from '../../../services/show-source/show-source.service';
import { LanguageChangeService } from 'src/services/language-change/language-change.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { LoggerService } from 'src/services/logger-services/logger.service';

/**
 * IntroSlidesComponent displays a set of onboarding slides
 * and handles dataset selection and user preferences before continuing.
 */
@Component({
  selector: 'app-intro-slides',
  templateUrl: './intro-slides.component.html',
  styleUrls: ['./intro-slides.component.scss']
})
export class IntroSlidesComponent implements OnInit {

  /** Username of the current user */
  @Input() userName: any;

  /** TenantID of the current user */
  @Input() tenantId: any;

  /** DomainID of the current user */
  @Input() domainId: any;

  /** List of available vector database datasets */
  @Input() vectorDbDatasets: any;

  /** Input Tour Enable Boolean value */
  @Input() isTourEnabled: boolean = false;

  /** Input Tour Status String value */
  @Input() tourStatus: string = "incomplete";

  /** Input Intro Wizard Enable Boolean value */
  @Input() isIntroWizardEnabled: boolean = false;

  /** Input Intro Wizard Status String value */
  @Input() introWizardStatus: any;

  /** Emits the selected dataset when intro is completed */
  @Output() selectedDatasetChange = new EventEmitter<any>();

  /** Emits when steps is completed or skipped */
  @Output() showIntroSlides = new EventEmitter<any>();

  /** Emits the name of the favorite dataset */
  @Output() favouriteDataset = new EventEmitter<string>();

  /** Emits the resized dropdown of the favorite dataset */
  @Output() resizeDropDownWidth = new EventEmitter<string>();

  /** Configuration data fetched from the chatbot service */
  configdata: any;

  /** Current step in the intro slides */
  currentStep = 0;

  /** Total number of steps in the intro */
  totalSteps = 4;


  /** Flag to show/hide source references */
  showSources = true;

  /** Selected dataset by the user */
  selectedDataset: any;

  /** Local copy of the username */
  localuserName: any;

  /** Flag to disable dataset selection if only one is available */
  isRadioDisabled: boolean = false;

   /** Flag to show show sources related HTML */
  showSlider: boolean = false;

  /** Flag to show show sources related HTML */
  introSlidesStatus: boolean = false;

  /** Error template for intro wizard */
  private readonly introWizardError = (error: any) => 
    `[INTRO WIZARD] - ${JSON.stringify(error)}`;

  /** Taking all the necessary services in the constructor */
  constructor(
    private readonly chatbotService: ChatbotService,
    private readonly selectedModuleService: SelectedModuleService,
    private readonly showSourceService: ShowSourceService,
    private readonly languageService: LanguageChangeService,
    private readonly translate: TranslateService,
    private readonly loggerService: LoggerService
  ) {}

  /** Lifecycle method called on component init */
  ngOnInit() {
    this.getConfigData();
    this.localuserName = this.userName;

    // Subscribe to source visibility changes
    this.showSourceService.showSources$.subscribe(value => {
      this.showSources = value;
      this.showSources = true;
      this.checkRadioButtonState();
    });
  }

  /** Disable radio buttons if only one dataset is available */
  checkRadioButtonState() {
    try {
      this.isRadioDisabled = this.vectorDbDatasets.length === 1;
      if (this.isRadioDisabled) {
        this.selectedDataset = this.vectorDbDatasets[0]; // Auto-select the only dataset
      }
    } catch (error) {
      this.loggerService.logError(this.introWizardError(error), "checkRadioButtonState");
    }
  }

  /** Handle toggle change for showing sources */
  onToggleChange() {
    this.showSourceService.setShowSources(this.showSources);
    this.showSources = true;
  }

  /** Fetch and prepare configuration data for the intro slides */
  getConfigData() {
    try{
      this.configdata = this.chatbotService.getConfigData();
      if (this.configdata.ReggieIntro) {
        this.configdata.ReggieIntro = this.configdata.ReggieIntro.replace(
          '{{display_dataset_name}}', 'Freya Fusion'
        );
      }
    } catch (error) {
      this.loggerService.logError(this.introWizardError(error), "getConfigData");
    }
  }

  /** Skip the intro and emit the required events */
  skipIntroMethod() {
    try {
      this.showSourceService.setShowSources(this.showSources = true);
      if (!this.selectedDataset) {
        this.selectedDataset = this.vectorDbDatasets[0];
      }
      localStorage.setItem('selectedVectorDbDataset', JSON.stringify(this.selectedDataset));
      this.resizeDropDownWidth.emit(this.selectedDataset)
      this.closeWizard();
    } catch (error) {
      this.loggerService.logError(this.introWizardError(error), "skipIntroMethod");
    }
  }

  /** Proceed to the next step or complete the intro */
  nextStep() {
    try {
      if (this.currentStep < this.totalSteps - 1) {
        this.currentStep++;
      } else {
        // Final step: emit selected dataset and finish intro
        if (!this.selectedDataset) {
          this.selectedDataset = this.vectorDbDatasets[0];
        } else {
          this.selectedDatasetChange.emit(this.selectedDataset);
          this.favouriteDataset.emit(this.selectedDataset.name);
        }

        this.showSourceService.setShowSources(this.showSources);
        localStorage.setItem('selectedVectorDbDataset', JSON.stringify(this.selectedDataset));
        this.selectedModuleService.changeSelectedValue(this.selectedDataset);
        this.resizeDropDownWidth.emit(this.selectedDataset)
        this.closeWizard();
      }
    } catch (error) {
      this.loggerService.logError(this.introWizardError(error), "nextStep");
    }
  }

  /**
   * Handle dataset selection from UI
   * @param module - Dataset module selected by the user
   */
  onDatasetSelect(module: any) {
    try {
      // Check if the module exists in vectorDbDatasets
      const moduleExists = this.vectorDbDatasets.some((dataset: any) => dataset.name === module.name);
      // Update selectedDataset based on the existence of the module
      if (!this.isRadioDisabled) {
        this.selectedDataset = moduleExists ? module : this.vectorDbDatasets[0];
      }
    } catch (error) {
      this.loggerService.logError(this.introWizardError(error), "onDatasetSelect");
    }
  }

  /**
   * Get CSS class for steps of progress bar in intro slides
   * @param index - Step index
   * @returns CSS class name
   */
  getDotClass(index: number): string {
    return index <= this.currentStep ? 'active' : '';
  }

  /** Update Greeting message text in intro slides */
  getGreetingMessage(): string {
    const greetingTemplate = this.translate.instant('GREETING_TEXT.HELLO_MESSAGE');
    return greetingTemplate.replace('{{UserFName}}', this.userName);
  }


  /** To close the intro slides on skip and reset values */
  closeWizard(){
    try {
      this.chatbotService.callUpdateWizardTour(
        environment.LoginUserID,
        this.tenantId,
        this.isIntroWizardEnabled,
        this.isTourEnabled,
        { status: this.tourStatus }, // Default API response
        { status: "completed" } // Setting Wizard as complete
      );
      const wizardStatus = "completed";
      this.showIntroSlides.emit(wizardStatus);
    } catch (error) {
      this.loggerService.logError(this.introWizardError(error), "closeWizard");
    }
  }
}